import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { AlertController, IonicModule } from '@ionic/angular';
import { firstValueFrom } from 'rxjs';
import { DataService } from '../data.service';

/**
 *  FOGADÓÓRA (backend/fogadoora.js).
 *
 *  Tanár: meghirdet egy fogadóórát (nap, mettől, meddig, hány percenként), látja a foglalásokat,
 *  letölti (CSV) vagy kinyomtatja, lemondhat. Gondviselő: a gyermeke nevében egy szabad időpontot
 *  foglal fogadóóránként, és lemondhatja. Hogy melyik nézet jelenik meg, a belépés módja dönti el.
 */
@Component({
  selector: 'app-fogadoora',
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule],
  templateUrl: './fogadoora.component.html',
  styleUrls: ['./fogadoora.component.scss'],
})
export class FogadooraComponent implements OnInit {
  /** A napló napi ablakába ágyazva (nyilvantarto): fejléc nélkül, mindig a tanári nézet. */
  @Input() beagyazott = false;
  /** Beágyazva: ennek a napnak a dátuma ('ÉÉÉÉ-HH-NN') az új fogadóóra alapértelmezett napja. */
  @Input() nap = '';
  mod: 'tanar' | 'szulo' | '' = '';
  intezmeny = '';
  betolt = false;
  fut = false;
  hiba = '';

  // tanár
  sajat: any[] = [];
  uj: any = { datum: '', kezdes: '16:00', vege: '18:00', idokoz_perc: 10, hely: '', megjegyzes: '' };

  // szülő
  gyermekek: any[] = [];
  fogadoorak: any[] = [];
  gyermek = '';
  csakTanarai = true;
  kereso = '';

  constructor(private http: HttpClient, private data: DataService, private alert: AlertController) {}

  async ngOnInit() {
    const sajat: any = await this.data.getFromStorage('sajatIntezmeny').catch(() => null);
    this.intezmeny = String(sajat?.intezmenyId || '');
    const loginmode = await this.data.getFromStorage('loginmode').catch(() => '');
    const jogkor = await this.data.getJogkor().catch(() => '');
    this.mod = this.beagyazott ? 'tanar' : ((loginmode === 'gondviselo' || jogkor === 'gondviselo') ? 'szulo' : 'tanar');
    const holnap = new Date(Date.now() + 86400000);
    this.uj.datum = /^\d{4}-\d{2}-\d{2}$/.test(this.nap) ? this.nap : holnap.toLocaleDateString('sv-SE');
    await this.toltes();
  }

  async toltes() {
    if (!this.intezmeny) { this.hiba = 'Nincs kiválasztott intézmény.'; return; }
    this.betolt = true; this.hiba = '';
    try {
      if (this.mod === 'tanar') {
        this.sajat = await firstValueFrom(this.http.get<any[]>(`/api/fogadoora/tanar/intezmeny/${this.intezmeny}`));
      } else {
        const r: any = await firstValueFrom(this.http.get(`/api/fogadoora/szulo/intezmeny/${this.intezmeny}`));
        this.gyermekek = r.gyermekek || [];
        this.fogadoorak = r.fogadoorak || [];
        if (!this.gyermek && this.gyermekek.length) this.gyermek = this.gyermekek[0].oktazon;
      }
    } catch (e: any) {
      this.hiba = e?.error?.error || 'Nem sikerült betölteni a fogadóórákat.';
    } finally { this.betolt = false; }
  }

  /** „2026. október 20., kedd” – a magyar locale nincs regisztrálva, ezért nem a date pipe. */
  napNev(d: string) {
    const x = new Date(`${d}T12:00:00`);
    return isNaN(x.getTime()) ? d : x.toLocaleDateString('hu-HU', { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' });
  }

  /* ---------------- tanár ---------------- */

  get ujDarab(): number {
    const p = (t: string) => { const m = String(t || '').match(/^(\d{1,2}):(\d{2})/); return m ? +m[1] * 60 + +m[2] : NaN; };
    const tol = p(this.uj.kezdes); const ig = p(this.uj.vege); const l = Number(this.uj.idokoz_perc);
    if (!(ig > tol) || !(l > 0)) return 0;
    return Math.floor((ig - tol) / l);
  }

  async meghirdet() {
    this.fut = true;
    try {
      const r: any = await firstValueFrom(this.http.post(`/api/fogadoora/tanar/intezmeny/${this.intezmeny}`, this.uj));
      this.data.sendToast(`Fogadóóra meghirdetve – ${r.idopont_db} időpont.`, 'success');
      await this.toltes();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'Nem sikerült meghirdetni.', 'error');
    } finally { this.fut = false; }
  }

  foglaltDb(f: any) { return (f.idopontok || []).filter((x: any) => x.foglalas).length; }

  private async okKerdes(header: string, message: string): Promise<string | null> {
    const a = await this.alert.create({
      header, message,
      inputs: [{ name: 'ok', type: 'text', placeholder: 'Indok (a szülő megkapja)' }],
      buttons: [{ text: 'Mégse', role: 'cancel' }, { text: 'Lemondás', role: 'ok' }],
    });
    await a.present();
    const { role, data } = await a.onDidDismiss();
    return role === 'ok' ? String(data?.values?.ok || '') : null;
  }

  async foglalasLemond(fg: any) {
    const ok = await this.okKerdes('Foglalás lemondása', `${fg.tanulo_nev || ''} (${fg.idopont}) – a szülő értesítést kap.`);
    if (ok === null) return;
    try {
      await firstValueFrom(this.http.post(`/api/fogadoora/tanar/foglalas/${fg.id}/lemond`, { ok }));
      await this.toltes();
    } catch (e: any) { this.data.sendToast(e?.error?.error || 'Nem sikerült lemondani.', 'error'); }
  }

  async fogadooraLemond(f: any) {
    const ok = await this.okKerdes('Fogadóóra lemondása', `${f.datum} ${f.kezdes}–${f.vege}: ${this.foglaltDb(f)} foglalás – minden érintett szülő értesítést kap.`);
    if (ok === null) return;
    try {
      await firstValueFrom(this.http.delete(`/api/fogadoora/tanar/fogadoora/${f.id}`, { body: { ok } }));
      await this.toltes();
    } catch (e: any) { this.data.sendToast(e?.error?.error || 'Nem sikerült lemondani.', 'error'); }
  }

  async csv(f: any) {
    try {
      const blob = await firstValueFrom(this.http.get(`/api/fogadoora/tanar/fogadoora/${f.id}/csv`, { responseType: 'blob' }));
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `fogadoora-${f.datum}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (_) { this.data.sendToast('Nem sikerült letölteni.', 'error'); }
  }

  /** Nyomtatható lista új ablakban (a foglalások a betöltött adatból). */
  nyomtat(f: any) {
    const e = (s: any) => String(s ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' } as any)[c]);
    const sorok = (f.idopontok || []).map((x: any) => {
      const g = x.foglalas || {};
      return `<tr><td>${e(x.ido)}</td><td>${e(g.tanulo_nev)}</td><td>${e(g.osztaly)}</td><td>${e(g.szulo_nev)}</td><td>${e(g.szulo_telefon || g.szulo_email)}</td><td>${e(g.megjegyzes)}</td></tr>`;
    }).join('');
    const w = window.open('', '_blank');
    if (!w) { this.data.sendToast('A böngésző letiltotta az új ablakot.', 'warning'); return; }
    w.document.write(`<!doctype html><html lang="hu"><head><meta charset="utf-8"><title>Fogadóóra ${e(f.datum)}</title>
      <style>body{font-family:sans-serif;margin:16px}table{border-collapse:collapse;width:100%}th,td{border:1px solid #999;padding:4px 6px;text-align:left;font-size:12px}</style></head>
      <body><h2>Fogadóóra – ${e(f.datum)} ${e(f.kezdes)}–${e(f.vege)}${f.hely ? ' · ' + e(f.hely) : ''}</h2>
      <table><thead><tr><th>Időpont</th><th>Tanuló</th><th>Osztály</th><th>Szülő</th><th>Elérhetőség</th><th>Megjegyzés</th></tr></thead><tbody>${sorok}</tbody></table>
      <script>window.onload=function(){window.print()}</script></body></html>`);
    w.document.close();
  }

  /* ---------------- szülő ---------------- */

  get szurtFogadoorak(): any[] {
    const q = this.kereso.trim().toLowerCase();
    return this.fogadoorak.filter((f) => (!this.csakTanarai || (f.gyermek_tanara || []).includes(this.gyermek) || (f.sajat_foglalasok || []).length)
      && (!q || String(f.tanar_nev || '').toLowerCase().includes(q)));
  }
  gyermekNev(okt: string) { return this.gyermekek.find((g) => g.oktazon === okt)?.nev || okt; }
  sajatEbben(f: any) { return (f.sajat_foglalasok || []).find((x: any) => x.oktazon === this.gyermek); }

  async foglal(f: any, ido: any) {
    if (!ido.szabad) return;
    const mar = this.sajatEbben(f);
    if (mar) { this.data.sendToast(`Erre a fogadóórára már foglaltál (${mar.idopont}) – előbb mondd le.`, 'warning'); return; }
    const a = await this.alert.create({
      header: 'Foglalás',
      message: `${f.tanar_nev} – ${f.datum} ${ido.ido} · ${this.gyermekNev(this.gyermek)}`,
      inputs: [{ name: 'megjegyzes', type: 'text', placeholder: 'Megjegyzés a tanárnak (nem kötelező)' }],
      buttons: [{ text: 'Mégse', role: 'cancel' }, { text: 'Foglalás', role: 'ok' }],
    });
    await a.present();
    const { role, data } = await a.onDidDismiss();
    if (role !== 'ok') return;
    try {
      await firstValueFrom(this.http.post(`/api/fogadoora/szulo/foglal/${f.id}`, { idopont: ido.ido, oktazon: this.gyermek, megjegyzes: data?.values?.megjegyzes || '' }));
      this.data.sendToast(`Lefoglalva: ${f.datum} ${ido.ido}`, 'success');
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'Nem sikerült foglalni.', 'error');
    }
    await this.toltes();
  }

  async lemond(f: any, fg: any) {
    const a = await this.alert.create({
      header: 'Foglalás lemondása', message: `${f.tanar_nev} – ${f.datum} ${fg.idopont}`,
      buttons: [{ text: 'Mégse', role: 'cancel' }, { text: 'Lemondás', role: 'ok' }],
    });
    await a.present();
    if ((await a.onDidDismiss()).role !== 'ok') return;
    try {
      await firstValueFrom(this.http.post(`/api/fogadoora/szulo/foglalas/${fg.id}/lemond`, {}));
      await this.toltes();
    } catch (e: any) { this.data.sendToast(e?.error?.error || 'Nem sikerült lemondani.', 'error'); }
  }
}
